import type { NumberFormatSpec } from '@highereducation/hen-ui';

/**
 * The named value formats every surface can rely on.
 *
 * Deliberately limited to formats that mean the same thing in any domain. A
 * token here is a shorthand this package can define without knowing what is
 * being measured: an integer is an integer whether it counts applications or
 * invoices.
 *
 * Domain vocabulary does **not** belong in this list, and the distinction is
 * not stylistic. A closed union defined here is a union every consumer inherits
 * — adding `gpa` to it means an invoicing surface has to carry `gpa` and cannot
 * add `currency` without changing this package. The union stays generic and
 * hosts resolve their own tokens to a spec before handing one over; see
 * {@link ValueFormat}.
 */
export const CHART_VALUE_FORMATS = [
  'integer',
  'decimal',
  'percent',
  'percent-delta',
  'raw',
] as const;

export type ChartValueFormat = (typeof CHART_VALUE_FORMATS)[number];

export function isChartValueFormat(value: unknown): value is ChartValueFormat {
  return (
    typeof value === 'string' &&
    (CHART_VALUE_FORMATS as readonly string[]).includes(value)
  );
}

/**
 * A format on an adapter config: one of the shared tokens, or a spec.
 *
 * The spec arm is how a host keeps its own vocabulary without this package
 * learning it. The dashboard's `gpa` and `duration-weeks` are resolved to a
 * spec by the dashboard, in the same config builder that already translates
 * authored props, so the authored document keeps its own words and only the
 * resolved meaning crosses the boundary.
 *
 * This is also why the spec arm is not a loophole for shipping formatter
 * functions: a `NumberFormatSpec` is data, so adapter output stays serializable
 * and the round-trip contract holds for a host token exactly as it does for a
 * shared one.
 */
export type ValueFormat = ChartValueFormat | NumberFormatSpec;

/**
 * Axis-tick formatting per named format. Percents carry no leading sign here
 * even for `'percent-delta'` — a signed axis reads as two scales — while the
 * tooltip keeps the sign, which is where the comparison is being made.
 */
const AXIS_FORMATS: Record<ChartValueFormat, NumberFormatSpec> = {
  integer: { roundToInteger: true, grouping: true },
  decimal: { fractionDigits: 2 },
  percent: { scale: 100, fractionDigits: 0, suffix: '%' },
  'percent-delta': { scale: 100, fractionDigits: 0, suffix: '%' },
  raw: {},
};

/**
 * Resolve a format to the spec a chart axis renders it with.
 *
 * Returns a spec rather than a formatter function on purpose: adapter output
 * has to stay serializable so it can be cached, snapshotted, and compared, and
 * the renderer is the last place that still knows how the value is displayed.
 * A spec handed in is already resolved and passes through unchanged.
 */
export function axisNumberFormat(
  format: ValueFormat | undefined,
): NumberFormatSpec | undefined {
  if (format === undefined) return undefined;
  return typeof format === 'string' ? AXIS_FORMATS[format] : format;
}

/**
 * Metric-text formatting per named format, for a KPI figure rather than an axis
 * tick.
 *
 * The same token means something different in the two places, which is why
 * there are two tables instead of one. An axis is a scale being read at a
 * glance, so it drops the fractional digit and the sign; a KPI figure is a
 * single number being read deliberately, so it keeps both — `'percent'` renders
 * `76.0%` where an axis renders `76%`, and `'percent-delta'` renders `+7.2%`
 * where an axis renders `7%`.
 *
 * `'raw'` has no spec: it means "print whatever is there", including values
 * that are not numbers, so the caller emits the text directly.
 */
const METRIC_FORMATS: Record<
  Exclude<ChartValueFormat, 'raw'>,
  NumberFormatSpec
> = {
  integer: { roundToInteger: true, grouping: true },
  decimal: { fractionDigits: 2, grouping: true },
  percent: { scale: 100, fractionDigits: 1, grouping: true, suffix: '%' },
  'percent-delta': {
    scale: 100,
    fractionDigits: 1,
    grouping: true,
    signDisplay: 'exceptZero',
    suffix: '%',
  },
};

/**
 * Resolve a format to the spec a KPI figure renders it with.
 *
 * A spec handed in passes through unchanged, which puts the burden of the
 * axis-versus-figure distinction on whoever resolved it. That is the right
 * place for it: a host that wants its own token rendered differently on the two
 * surfaces knows which one it is building a config for, and a host that does
 * not can hand the same spec to both.
 */
export function metricNumberFormat(
  format: ValueFormat | undefined,
): NumberFormatSpec | undefined {
  if (format === undefined) return undefined;
  if (typeof format !== 'string') return format;
  return format === 'raw' ? undefined : METRIC_FORMATS[format];
}
